import React, { useContext, useEffect, useMemo, useState } from "react";

import Breadcrumbs from "~/components/Breadcrumbs";
import ChartFinancialOutstanding from "~/components/Charts/FinancialOutstanding";
import FiltersBar from "~/components/FiltersBar";
import PageHeader from "~/components/PageHeader";
import useReportsStore from "~/store/reportsStore";
import ChartDefaultRate from "./Reports/chartDelinquecy";
import DefaultRateFilter from "./Reports/filters/defaultFilter";
import { getCurrentPage } from "~/functions";
import ChartGrossValues from "~/components/Charts/GrossValues";
import ChartNetValues from "~/components/Charts/NetValues";
import ChartExpenses from "~/components/Charts/Expenses";
import FilialBreakdownChart from "~/components/Charts/GrossValues/FilialBreakdownChart";
import ProfitLossCompareChart from "~/components/Charts/NetValues/CompareChart";
import ExpensesCompareChart from "~/components/Charts/Expenses/CompareChart";
import { FullGridContext } from ".";
import PeriodFilter from "~/components/Charts/components/periodFilter";
import api from "~/services/api";

const dateTypeOptions = [
  { value: "accrual_date", label: "Accrual Date" },
  { value: "settlement_date", label: "Settlement Date" },
  { value: "due_date", label: "Due Date" },
];

export default function FinancialDashboard() {
  const { filters } = useReportsStore();
  const currentPage = getCurrentPage();
  const [filialBreakdown, setFilialBreakdown] = useState({ show: false, data: [] });
  const [profitLossCompare, setProfitLossCompare] = useState({ show: false, data: null });
  const [expensesCompare, setExpensesCompare] = useState({ show: false, data: [] });
  const [dreConfigs, setDreConfigs] = useState([]);
  const [selectedDreConfig, setSelectedDreConfig] = useState("");
  const [dateType, setDateType] = useState("due_date");
  const [dateTypeTouched, setDateTypeTouched] = useState(false);

  const {
    accessModule,
    activeFilters,
    gridData,
    gridDetails,
    gridHeader,
    handleFilters,
    handleOpened,
    limit,
    loadingData,
    opened,
    orderBy,
    page,
    pages,
    search,
    setActiveFilters,
    setGridData,
    setGridDetails,
    setGridHeader,
    setLimit,
    setLoadingData,
    setOpened,
    setOrderBy,
    setPage,
    setPages,
    setSearch,
    setTotalRows,
    setSuccessfullyUpdated,
    successfullyUpdated,
    totalRows,
  } = useContext(FullGridContext);

  const pageAccess = accessModule.children.find(
    (el) => el.alias === "financial-dashboard"
  );

  const hasAccessToAlias = (alias) => {
    return pageAccess.children.find((el) => el.alias === alias)
      ?.MenuHierarchyXGroup?.view;
  };
  const overlayActive = filialBreakdown.show || profitLossCompare.show || expensesCompare.show;
  const isFilialActive = filialBreakdown.show;
  const isProfitLossActive = profitLossCompare.show;
  const isExpensesActive = expensesCompare.show;
  const expensesPeriod = filters.periodsByReport?.Expenses || filters.period;
  const isComparePeriod =
    expensesPeriod?.label === "Month vs Month" ||
    expensesPeriod?.label === "Year vs Year";
  const openFilialBreakdown = (data) => {
    setProfitLossCompare({ show: false, data: null });
    setExpensesCompare({ show: false, data: [] });
    setFilialBreakdown(data);
  };
  const openProfitLossCompare = (data) => {
    if (profitLossCompare.show) return;
    setFilialBreakdown({ show: false, data: [] });
    setExpensesCompare({ show: false, data: [] });
    setProfitLossCompare({ show: true, data });
  };
  const openExpensesCompare = (data) => {
    if (expensesCompare.show) return;
    setFilialBreakdown({ show: false, data: [] });
    setProfitLossCompare({ show: false, data: null });
    setExpensesCompare({ show: true, data });
  };

  useEffect(() => {
    async function fetchDreConfigs() {
      try {
        const { data } = await api.get("/dreconfigs-getall");
        setDreConfigs(data || []);
        if (data && data.length > 0) {
          setSelectedDreConfig(data[0].id);
          setDateType(data[0]?.date_type || "due_date");
          setDateTypeTouched(false);
        }
      } catch (e) {
        setDreConfigs([]);
      }
    }
    fetchDreConfigs();
  }, []);

  const effectiveDateType = useMemo(() => {
    const cfg = (dreConfigs || []).find(
      (c) => String(c.id) === String(selectedDreConfig)
    );
    return dateTypeTouched ? dateType : (cfg?.date_type || dateType || "due_date");
  }, [dreConfigs, selectedDreConfig, dateType, dateTypeTouched]);

  const renderConfigControls = () => (
    <div className="flex flex-row gap-3">
      <div className="flex flex-col gap-1">
        <label className="text-xs font-semibold text-gray-600">IS Configuration</label>
        <select
          value={selectedDreConfig}
          onChange={(e) => {
            const value = e.target.value;
            setSelectedDreConfig(value);
            const cfg = (dreConfigs || []).find(
              (c) => String(c.id) === String(value)
            );
            setDateType(cfg?.date_type || "due_date");
            setDateTypeTouched(false);
          }}
          className="px-2 py-1.5 text-xs border rounded-sm focus:ring-1 focus:ring-mila_orange"
        >
          {dreConfigs.length === 0 ? (
            <option value="">No configurations available</option>
          ) : (
            dreConfigs.map((config) => (
              <option key={config.id} value={config.id}>
                {config.dre_name} ({config.dre_code})
              </option>
            ))
          )}
        </select>
      </div>
      <div className="flex flex-col gap-1">
        <label className="text-xs font-semibold text-gray-600">Date Type</label>
        <select
          value={effectiveDateType}
          onChange={(e) => {
            setDateType(e.target.value);
            setDateTypeTouched(true);
          }}
          className="px-2 py-1.5 text-xs border rounded-sm focus:ring-1 focus:ring-mila_orange"
        >
          {dateTypeOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>
    </div>
  );

  return (
    <div className="h-full bg-white flex flex-1 flex-col justify-start items-start rounded-tr-2xl px-4 overflow-hidden">
      <PageHeader>
        <Breadcrumbs currentPage={currentPage} />
        <FiltersBar></FiltersBar>
      </PageHeader>

      <div className="flex-1 w-full flex flex-col pt-6 overflow-x-auto overflow-y-auto">
        <div className="min-w-[1200px] w-full">
          <div className="mb-4">
            {renderConfigControls()}
          </div>
          <div className="flex flex-row w-full items-start justify-start gap-4 min-h-[112px]">
            {hasAccessToAlias("invoices-chart") && (
              <div className={`flex flex-col justify-start min-w-[420px] max-w-[560px] flex-1 transition-opacity duration-300 ${
                isProfitLossActive ? "opacity-30 pointer-events-none" : "opacity-100"
              }`}>
                <PeriodFilter report="GrossValues" />
              </div>
            )}
            {hasAccessToAlias("profit-and-loss-chart") && (
              <div className={`flex flex-col justify-start min-w-[420px] max-w-[560px] flex-1 transition-opacity duration-300 ${
                isFilialActive ? "opacity-30 pointer-events-none" : "opacity-100"
              }`}>
                <PeriodFilter report="Profit&Loss" />
              </div>
            )}
            {hasAccessToAlias("expenses-chart") && (
              <div className={`flex flex-col justify-start min-w-[420px] max-w-[560px] flex-1 transition-opacity duration-300 ${
                isFilialActive || isProfitLossActive ? "opacity-30 pointer-events-none" : "opacity-100"
              }`}>
                <PeriodFilter report="Expenses" />
              </div>
            )}
          </div>

          <div className="flex flex-row w-full items-start justify-start gap-4">
            {hasAccessToAlias("invoices-chart") && (
              <div className={`min-w-[420px] max-w-[560px] flex-1 transition-opacity duration-300 ${
                isProfitLossActive || isExpensesActive ? "opacity-30 pointer-events-none" : "opacity-100"
              }`}>
                <ChartGrossValues
                  onFilialBreakdownChange={openFilialBreakdown}
                  dreConfigId={selectedDreConfig}
                  dateType={effectiveDateType}
                />
              </div>
            )}
            {hasAccessToAlias("profit-and-loss-chart") && (
              <div className={`min-w-[420px] max-w-[560px] flex-1 transition-opacity duration-300 ${
                isFilialActive || isExpensesActive ? "opacity-30 pointer-events-none" : "opacity-100"
              }`}>
                <ChartNetValues
                  dreConfigId={selectedDreConfig}
                  dateType={effectiveDateType}
                  onCompareToggle={(data) => {
                    openProfitLossCompare(data);
                  }}
                />
              </div>
            )}
            {hasAccessToAlias("expenses-chart") && (
              <div className={`min-w-[420px] max-w-[560px] flex-1 transition-opacity duration-300 ${
                isFilialActive || isProfitLossActive ? "opacity-30 pointer-events-none" : "opacity-100"
              }`}>
                <ChartExpenses
                  dreConfigId={selectedDreConfig}
                  dateType={effectiveDateType}
                  onCompareToggle={
                    isComparePeriod
                      ? (data) => {
                          openExpensesCompare(data);
                        }
                      : null
                  }
                />
              </div>
            )}
          </div>

          {filialBreakdown.show && (
            <FilialBreakdownChart
              data={filialBreakdown.data}
              onClose={() => setFilialBreakdown({ show: false, data: [] })}
            />
          )}
          {profitLossCompare.show && (
            <ProfitLossCompareChart
              data={profitLossCompare.data}
              onClose={() => setProfitLossCompare({ show: false, data: null })}
            />
          )}
          {expensesCompare.show && (
            <ExpensesCompareChart
              data={expensesCompare.data}
              onClose={() => setExpensesCompare({ show: false, data: [] })}
            />
          )}

          {hasAccessToAlias("received-and-outstanding-chart") && (
            <div className={`flex flex-row w-full items-start justify-start gap-4 transition-opacity duration-300 ${
              overlayActive ? "opacity-30 pointer-events-none" : "opacity-100"
            }`}>
              <ChartFinancialOutstanding />
            </div>
          )}

          {hasAccessToAlias("delinquency-index-chart") && (
            <div className={`transition-opacity duration-300 ${
              overlayActive ? "opacity-30 pointer-events-none" : "opacity-100"
            }`}>
              <div className="w-full pt-6">
                <DefaultRateFilter />
              </div>

              <div className="pb-6">
                <ChartDefaultRate filters={filters} />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
